import React, { useEffect } from 'react';
import { SEO } from '../components/SEO';
import { useLocation } from 'react-router-dom';
import { Check, Target, Eye, Award } from 'lucide-react';
import SectionHeader from '../components/SectionHeader';

export const About = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  const coreValues = [
    {
      title: 'Integrity',
      description: 'We uphold the highest standards of honesty, transparency, and ethical conduct in every client engagement, regulatory submission, and business relationship.',
      icon: <Check />,
    },
    {
      title: 'Excellence',
      description: 'We are committed to delivering work of the highest quality - accurate, thorough, and strategically sound. Our submissions are submission-ready.',
      icon: <Award />,
    },
    {
      title: 'Expertise',
      description: 'Our team brings deep technical and regulatory knowledge across pharmacology, engineering, quality management, and clinical research.',
      icon: <Target />,
    },
    {
      title: 'Client Focus',
      description: 'Every engagement begins with understanding your business, your goals, and your constraints. We build solutions that work for you.',
      icon: <Eye />,
    },
    {
      title: 'Adaptability',
      description: 'Regulatory landscapes change. Approvals require pivots. Markets evolve. We remain agile, proactive, and resourceful.',
      icon: <Check />,
    },
  ];

  const teamMembers = [
    {
      name: 'Pravin Jagtap',
      role: 'Co-Founder & Director',
      image: 'https://customer-assets.emergentagent.com/job_pharma-compliance-7/artifacts/gceaovph_image.png',
      bio: null,
      bullets: [
        'M. Pharm. in Quality Assurance (Pune University), B. Pharm., D. Pharm.',
        '18+ years in pharmaceutical regulatory affairs',
        'Worked at Cipla Ltd., Centaur Pharmaceuticals, Glenmark Pharmaceuticals, Bilcare Limited',
        'Expert in FDA licensing, WHO GMP, CoPP, CDSCO inspections',
        'Handled applications for additional products, technical staff approvals, test licences, tender certificates',
        'Faced WHO-GMP, MHRA, USFDA, DACA-Ethiopia audits successfully',
        'WHO GMP & CoPP approvals for nearly 30 pharma companies',
        'Serving 100+ small to medium scale clients for regulatory & QMS support',
        'International exposure: S. Korea, pharma expos, global trading',
      ],
    },
    {
      name: 'Jayesh Salunke',
      role: 'Co-Founder & Director',
      image: 'https://customer-assets.emergentagent.com/job_pharma-compliance-7/artifacts/wp2jdggj_image.png',
      bio: null,
      bullets: [
        'M. Pharm. in Quality Assurance (Rajiv Gandhi University), B. Pharm. (Pune University)',
        '17+ years in pharmaceutical regulatory affairs',
        'Worked at Genpharma Int Pvt. Ltd., Centaur Pharmaceuticals',
        'Expert in FDA licensing, WHO GMP, CoPP, CDSCO compliance',
        'Handled applications for additional products, technical staff approvals, test licences',
        'Knowledge of D&C Act, Govt. Circulars, online regulatory applications',
        'International drug sourcing from USA, EU, Australia, Brazil for clinical trials',
        'Filed first successful WHO GMP & CoPP application for Genpharm',
        'Served 20+ small to medium scale partners through FDA services',
        'Faced WHO-GMP, MHRA, USFDA, DACA audits successfully',
      ],
    },
  ];

  return (
    <div className="pt-[72px]">
      <SEO page="about" />
      {/* Hero */}
      <section
        className="relative py-24"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_pharma-compliance-7/artifacts/pi6dqgso_02%20global-reach-banner.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, #1570EF 0%, #1570EFee 35%, #1570EFaa 55%, transparent 75%)' }} />
        <div className="relative z-10 max-w-[1280px] mx-auto px-4 sm:px-6">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white max-w-2xl" >About Pegasus Clinicare</h1>
          <p className="text-xl max-w-2xl text-white/90" >
            Decade-Strong Regulatory Expertise. Built on Integrity. Driven by Results.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section id="story" className="py-24 bg-white" data-testid="our-story-section">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          <SectionHeader label="OUR STORY" title="Our Story" />
          <div className="max-w-4xl mx-auto space-y-6 text-base sm:text-lg" style={{ color: '#1E293B' }}>
            <p>
              Pegasus Clinicare Pvt. Ltd. was founded in 2014 with a single, unwavering purpose: to make regulatory compliance achievable, transparent, and strategic for every business we serve. In a regulatory environment that grows more complex each year - with evolving CDSCO guidelines, international harmonization requirements, and increasingly rigorous manufacturing standards - we recognized a critical gap: companies needed not just a documentation service, but a true strategic partner who understood both the science and the business of compliance.
            </p>
            <p>
              From our early years supporting Indian pharmaceutical manufacturers with CDSCO submissions, we grew steadily by expanding our expertise across medical devices, cosmetics, food industries, and US FDA regulatory pathways. We added facility design and turnkey project consultancy to address the critical need for GMP-compliant infrastructure guidance. And we built a global drug sourcing vertical to support clinical research organizations and BA/BE study teams with hard-to-source innovator and reference listed drugs.
            </p>
            <p>
              Today, Pegasus Clinicare is a multi-disciplinary regulatory powerhouse - trusted by over 50 reputed clients across 7+ industry sectors, from startups building their first regulatory strategy to multinational organizations navigating complex multi-market submissions. Our founding philosophy remains at the core of everything we do: regulatory expertise should empower businesses to grow, innovate, and serve patients and consumers without barriers.
            </p>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section id="vision" className="py-24" style={{ backgroundColor: '#EFF6FF' }} data-testid="vision-mission-section">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          <SectionHeader label="OUR FOUNDATION" title="Vision & Mission" />
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-white p-8 rounded-2xl border-2 border-[#1570EF] shadow-lg">
              <div className="w-16 h-16 bg-[#1570EF] rounded-full flex items-center justify-center mb-6">
                <Eye size={32} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#1B3A6B' }}>
                Vision
              </h3>
              <p className="text-base" style={{ color: '#1E293B' }}>
                To be a globally recognized and trusted regulatory consulting and drug sourcing partner - enabling efficient market access and supporting innovation across pharmaceutical, medical device, cosmetic, and allied industries worldwide.
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl border-2 border-[#0E7C86] shadow-lg">
              <div className="w-16 h-16 bg-[#0E7C86] rounded-full flex items-center justify-center mb-6">
                <Target size={32} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#1B3A6B' }}>
                Mission
              </h3>
              <p className="text-base" style={{ color: '#1E293B' }}>
                Pegasus Clinicare Pvt. Ltd. is committed to providing strategic, compliant, and efficient regulatory and consulting services that simplify complex regulatory pathways, accelerate approvals, and ensure the successful commercialization of products in global markets. We achieve this by combining technical excellence, regulatory depth, and an unwavering focus on client outcomes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 bg-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          <SectionHeader label="CORE VALUES" title="What We Stand For" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coreValues.map((value, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl border border-[#BFDBFE] hover:border-[#1570EF] hover:shadow-xl transition-all duration-300"
              >
                <div className="w-12 h-12 bg-[#1570EF] rounded-full flex items-center justify-center mb-4">
                  <div className="text-white">{value.icon}</div>
                </div>
                <h3 className="text-lg font-bold mb-3" style={{ color: '#1B3A6B' }}>
                  {value.title}
                </h3>
                <p className="text-sm" style={{ color: '#1E293B' }}>
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Policy */}
      <section id="quality" className="py-24" style={{ backgroundColor: '#EFF6FF' }} data-testid="quality-policy-section">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          <SectionHeader label="QUALITY POLICY" title="Our Commitment to Quality" />
          <div className="max-w-4xl mx-auto">
            <p className="text-base sm:text-lg mb-8" style={{ color: '#1E293B' }}>
              Pegasus Clinicare Pvt. Ltd. is committed to delivering regulatory and consulting services that meet all applicable statutory and regulatory requirements. We ensure accuracy, consistency, and compliance in every deliverable, and we continuously improve our processes to enhance client satisfaction.
            </p>
            <div className="bg-white p-8 rounded-2xl border border-[#BFDBFE]">
              <h3 className="text-xl font-bold mb-6" style={{ color: '#0E7C86' }}>
                Our Quality Objectives:
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Check size={24} className="text-[#1570EF] flex-shrink-0 mt-0.5" />
                  <span className="text-base" style={{ color: '#1E293B' }}>
                    Ensure full compliance with all applicable regulatory standards in every service we provide.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check size={24} className="text-[#1570EF] flex-shrink-0 mt-0.5" />
                  <span className="text-base" style={{ color: '#1E293B' }}>
                    Achieve and maintain a high level of client satisfaction through timely, accurate, and professional service delivery.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check size={24} className="text-[#1570EF] flex-shrink-0 mt-0.5" />
                  <span className="text-base" style={{ color: '#1E293B' }}>
                    Deliver projects within agreed timelines without compromising quality or compliance.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check size={24} className="text-[#1570EF] flex-shrink-0 mt-0.5" />
                  <span className="text-base" style={{ color: '#1E293B' }}>
                    Continuously improve employee competency through structured training, mentoring, and professional development.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check size={24} className="text-[#1570EF] flex-shrink-0 mt-0.5" />
                  <span className="text-base" style={{ color: '#1E293B' }}>
                    Maintain and continuously strengthen an effective Quality Management System (QMS) across all functions.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Our Team */}
      <section id="team" className="py-24 bg-white" data-testid="team-section">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          <SectionHeader label="OUR TEAM" title="Meet the Pegasus Team" />
          <p className="text-center text-base sm:text-lg mb-12 max-w-3xl mx-auto" style={{ color: '#1E293B' }}>
            Our strength lies in our people. The Pegasus team is composed of experienced professionals from pharmacy, engineering, quality management, regulatory affairs, and clinical research disciplines - each bringing technical depth, regulatory insight, and a commitment to excellence.
          </p>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {teamMembers.map((member, index) => (
              <div
                key={index}
                data-testid={`team-member-${index}`}
                className="bg-white p-8 rounded-2xl border border-[#BFDBFE] hover:border-[#1570EF] hover:shadow-xl transition-all duration-300 text-center"
              >
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-32 h-32 rounded-full mx-auto mb-6 object-cover border-4 border-[#1570EF]"
                />
                <h3 className="text-xl font-bold mb-2" style={{ color: '#1B3A6B' }}>
                  {member.name}
                </h3>
                <div className="text-sm font-semibold mb-4" style={{ color: '#0E7C86' }}>
                  {member.role}
                </div>
                <div className="text-sm text-left" style={{ color: '#1E293B' }}>
                  {member.bio && <p>{member.bio}</p>}
                  {member.bullets && (
                    <ul className="space-y-1 mt-2">
                      {member.bullets.map((b, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-[#1570EF] mt-1 flex-shrink-0">&#8226;</span>
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
